<template>
  <article
    ref="el"
    v-viewport.25%.no-out.once="onViewport"
    class="rounded-3xl overflow-hidden text-white group shadow-md hover:shadow-none transition duration-500"
    :class="{
      'theme-petrol': theme === 'petrol',
      'theme-purple': theme === 'purple',
      'theme-red': theme === 'red',
      'theme-ocher-yellow': theme === 'ocher-yellow',
      'theme-dark-green': theme === 'dark-green' || !theme,
      'theme-blue': theme === 'blue',
      'theme-pink': theme === 'pink',
      'theme-peach': theme === 'peach',
      'theme-light-blue': theme === 'light-blue',
    }"
    data-component="CardBanner"
  >
    <AtomLink :url="isQuestionnairePromo ? undefined : buttonUrl" class="c-banner flex flex-col sm:flex-row cursor-pointer" @click="onClick">
      <div v-if="image" class="sm:order-2 shrink-0 sm:ml-auto overflow-hidden sm:w-1/2 lg:w-auto">
        <MoleculePicture :image="image" class="w-full h-full block group-hover:scale-110 transition duration-500" :aria-hidden="true" />
      </div>
      <div class="px-5 py-6 sm:p-8 flex flex-auto">
        <div class="flex flex-col justify-center items-start">
          <h5 v-if="topLabel" class="text-sm font-medium leading-tight mb-2" :class="{ 'sm:text-base': !image, 'lg:text-base': image }">{{ topLabel }}</h5>
          <h4 class="text-xl font-semibold leading-tight mb-2" :class="{ 'sm:text-3xl': !image, 'lg:text-3xl': image }">{{ title }}</h4>
          <h6 v-if="subLabel" class="text-sm font-semibold leading-tight">{{ subLabel }}</h6>
          <MoleculeButton v-if="buttonUrl && buttonTitle" size="medium" class="mt-4" :class="{ 'sm:hidden': !image, 'lg:hidden': image }">{{ buttonTitle }}</MoleculeButton>
          <MoleculeButton v-if="buttonUrl && buttonTitle" class="mt-4 hidden" :class="{ 'sm:inline-flex': !image, 'lg:inline-flex': image }">{{ buttonTitle }}</MoleculeButton>
        </div>
        <div v-if="icon" class="shrink-0 inline-flex items-end sm:items-center pl-4 lg:px-20 ml-auto">
          <AtomIcon :icon="icon" classes="w-20 h-20 sm:w-32 sm:h-32 md:w-40 md:h-40" />
        </div>
      </div>
    </AtomLink>
  </article>
</template>

<script setup lang="ts">
const props = defineProps({
  title: { type: String, required: true },
  topLabel: { type: String, required: false, default: '' },
  subLabel: { type: String, required: false, default: '' },
  icon: { type: String, required: false, default: '' },
  image: { type: Object as PropType<Record<string, any>>, required: false, default: () => undefined },
  buttonTitle: { type: String, required: false, default: '' },
  buttonUrl: { type: String, required: false, default: '' },
  theme: { type: String, required: true },
  // Optional on purpose: the series-overview and discovery-collection banners are never questionnaire
  // promos, so they omit it rather than each opting out explicitly.
  isQuestionnairePromo: { type: Boolean, required: false, default: false },
})

const router = useRouter()
const recommendationsStore = useRecommendationsStore()

const el = ref()
const getPosition = () => (el.value?.getBoundingClientRect()?.top || 0) + (window.pageYOffset || 0)

const promoView = setupGtmPromoView()
const onViewport = () =>
  promoView([
    {
      name: props.title,
      creative: [props.topLabel, props.subLabel].join(' | '),
      position: getPosition(),
    },
  ])

const promoClick = setupGtmPromoClick()
const onClick = () => {
  promoClick([
    {
      name: props.title,
      creative: [props.topLabel, props.subLabel].join(' | '),
      position: getPosition(),
    },
  ])

  if (props.isQuestionnairePromo) {
    recommendationsStore.reset()
    router.push({ path: props.buttonUrl })
  }
}
</script>

<style lang="scss" scoped>
.c-banner {
  min-height: 165px;

  @screen sm {
    min-height: 265px;
  }
}

.theme-dark-green {
  background: radial-gradient(
    112.53% 588.19% at -20.53% -39.62%,
    theme('colors.app.secondary-9-green--sherpa-blue') 0%,
    theme('colors.app.secondary-8-blue--medium-aquamarine') 100%
  );
}

.theme-petrol {
  background: radial-gradient(34.2% 132.91% at 100% 100%, theme('colors.app.secondary-9-blue--turquoise') 0%, theme('colors.app.secondary-9-blue--iris') 99.32%);
}

.theme-peach {
  background: radial-gradient(112.53% 588.19% at -20.53% -39.62%, theme('colors.app.secondary-6-red--french-rose') 0%, theme('colors.app.secondary-7-violet--purple-heart') 100%);
}

.theme-red {
  background: radial-gradient(112.53% 588.19% at -20.53% -39.62%, theme('colors.app.primary-1-red--fire-brick') 0%, theme('colors.app.primary-1-red--torch') 100%);
}

.theme-light-green {
  background: radial-gradient(
    112.53% 588.19% at -20.53% -39.62%,
    theme('colors.app.secondary-8-blue--medium-aquamarine') 0%,
    theme('colors.app.secondary-8-green--vista-blue') 100%
  );
}

.theme-blue {
  background: radial-gradient(112.53% 588.19% at -20.53% -39.62%, theme('colors.app.secondary-5-blue--onahau') 0%, theme('colors.app.secondary-5-blue--prussian') 100%);
}

.theme-pink {
  background: radial-gradient(112.53% 588.19% at -20.53% -39.62%, theme('colors.app.secondary-6-red--azalea') 0%, theme('colors.app.secondary-6-red--french-rose') 100%);
}

.theme-purple {
  background: radial-gradient(112.53% 588.19% at -20.53% -39.62%, theme('colors.app.secondary-7-violet--fog') 0%, theme('colors.app.secondary-7-violet--purple-heart') 100%);
}

.theme-ocher-yellow {
  background: radial-gradient(
    112.53% 588.19% at -20.53% -39.62%,
    theme('colors.app.secondary-8-blue--medium-aquamarine') 0%,
    theme('colors.app.secondary-8-green--vista-blue') 100%
  );
}

.theme-light-blue {
  background: radial-gradient(112.53% 588.19% at -20.53% -39.62%, theme('colors.app.secondary-10-blue--bright-sky') 0%, theme('colors.app.secondary-10-blue--royal-blue') 100%);
}
</style>
